import {
  PlusCircleOutlined,
  GiftOutlined,
  TrophyOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  TreeSelect,
  DatePicker,
  message,
  Divider,
  Switch,
  Space,
  Typography,
  Upload,
  Image,
  InputNumber,
  Tooltip,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createGiftExchange,
  getListGiftExchange,
} from "../../../api/giftExchange/giftExchange";
import {
  getListDataGift,
  getListTelcoDataGift,
} from "../../../api/giftExchange/dataGift";
import { store } from "../../../redux/configStore";
import { transformData } from "../../../utils/recursive";
import useStoreData from "../../../hooks/useStoreData";
import ProductSelectorModal from "./components/ProductSelectorModal";
import ProductForm from "../Product/components/ProductForm";
import { resolveImageUrl } from "../../../utils/image";
import { categoryAPIWithErrorHandling } from "../../../api/product/category";
import FormShell from "../../../components/common/FormShell";
import { neutral, brand, semantic, spacing, radius, typography, border } from "../../../theme/tokens";

const { Text } = Typography;

const sectionTitle = {
  fontSize: typography.size.md,
  fontWeight: typography.weight.semibold,
  color: neutral[900],
  marginBottom: spacing[5],
  letterSpacing: "-0.005em",
};

export default function FormAddGiftExchangeTable({
  isModalOpen,
  currentPage,
  pageSize,
  handleOk,
  handleCancel,
  asPage = false,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [branchId, setBranchId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [unlimited, setUnlimited] = useState(false);
  const [giftType, setGiftType] = useState("PRODUCT");
  const dispatch = useDispatch();
  const [selectedTelco, setSelectedTelco] = useState(null);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [fileList, setFileList] = useState([]);

  const [productQuantities, setProductQuantities] = useState({}); // { productId: quantity }
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState([]);
  const [isInventoryCheck, setIsInventoryCheck] = useState(true);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();
  const { listTelcoDataGifts } = useSelector(
    (state) => state.persistedReducer.telcoDataGiftSlice
  );
  const { listDataGifts } = useSelector(
    (state) => state.persistedReducer.dataGiftSlice
  );

  const storeOptions = transformData(allListStore?.data)?.filter(
    (item) => item.parent_id === 0
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryAPIWithErrorHandling.getList({ per_page: 100 });
        if (res.success) setCategories(res.data.data || []);
      } catch (e) { console.error("Lỗi tải danh mục"); }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          store.dispatch(getListTelcoDataGift({ currentPage: 1, pageSize: 100 })),
        ];
        await Promise.all(requests);
      } catch (error) {
        message.error("Không thể tải dữ liệu!");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTelco) {
      store.dispatch(
        getListDataGift({ currentPage: 1, pageSize: 100, telco: selectedTelco })
      );
    }
  }, [selectedTelco]);

  const handleFormSubmit = async (values) => {
    setIsLoading(true);

    if (giftType === "PRODUCT" && (!selectedProductDetails || selectedProductDetails.length === 0)) {
        message.error("Vui lòng chọn ít nhất một sản phẩm!");
        setIsLoading(false);
        return;
    }

    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (key === "start_date" || key === "end_date") {
        formData.append(key, values[key].format("YYYY-MM-DD HH:mm:ss"));
      } else if (key === "image") {
        if (fileList.length > 0 && fileList[0].originFileObj) {
          formData.append("image", fileList[0].originFileObj);
        }
      } else if (key === "products") {
        // Bỏ qua — products được append riêng bên dưới từ state
      } else {
        formData.append(key, values[key]);
      }
    });

    // Append products từ state (gửi từng product riêng để Laravel parse thành array)
    if (giftType === "PRODUCT") {
      selectedProductDetails.forEach((product, index) => {
        formData.append(`products[${index}][id]`, product.id);
        formData.append(`products[${index}][quantity]`, productQuantities[product.id] || 1);
      });
    }

    formData.append("status", isActive ? 1 : 0);
    formData.append("unlimited", unlimited ? 1 : 0);
    formData.append("is_inventory_check", isInventoryCheck ? 1 : 0);

    if (giftType === "DATA") {
      formData.append("datas[0][telco]", values.telco);
      formData.append("datas[0][data_pack]", values.data_pack);
    }

    try {
      const response = await dispatch(createGiftExchange(formData));
      if (createGiftExchange.fulfilled.match(response)) {
        message.success("Thêm mới thành công");
        await dispatch(getListGiftExchange({ currentPage, pageSize }));
        handleOk();
        form.resetFields();
        setFileList([]);
        setSelectedProductDetails([]);
        setProductQuantities({});
      } else {
         if (response.payload.errors) {
          Object.keys(response.payload.errors).forEach((field) => {
            const errorMessages = response.payload.errors[field];
            errorMessages.forEach((errorMessage) => {
              message.error(`${errorMessage}`);
            });
          });
        } else if (response.payload.error) {
          message.error(response.payload.error);
        }
      }
    } catch (err) {
      console.error("Có lỗi xảy ra:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || "Xem trước hình ảnh");
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Chỉ chấp nhận file hình ảnh!");
    }
    return isImage || Upload.LIST_IGNORE;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Tải ảnh</div>
    </div>
  );

  return (
    <FormShell
      asPage={asPage}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          <GiftOutlined style={{ fontSize: 18, color: brand.primary }} />
          <span style={{ fontSize: typography.size.lg, fontWeight: typography.weight.semibold, color: neutral[900] }}>
            Thêm chương trình quà tặng mới
          </span>
        </div>
      }
      open={isModalOpen}
      footer={[
        <Button key="cancel" onClick={handleCancel} style={{ minWidth: 100 }}>
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<PlusCircleOutlined />}
          loading={isLoading}
          onClick={() => form.submit()}
          style={{ minWidth: 140 }}
        >
          Tạo mới
        </Button>,
      ]}
      onCancel={handleCancel}
      width="75%"
      styles={{
        body: {
          padding: spacing[6],
          maxHeight: "calc(100vh - 150px)",
          overflow: "auto",
        },
        header: {
          borderBottom: border.hairline,
          paddingBottom: spacing[4],
          marginBottom: 0,
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFormSubmit}
        requiredMark={false}
        initialValues={{
          type: "PRODUCT",
          is_inventory_check: true,
        }}
      >
        {/* Section 1: Thông tin cơ bản & Hình ảnh */}
        <div style={sectionTitle}>Thông tin cơ bản & Hình ảnh</div>
        <Row gutter={[spacing[6], spacing[4]]} align="top">
          <Col xs={24} md={8}>
            <Form.Item
              label="Hình ảnh chương trình"
              name="image"
              rules={[{ required: true, message: "Vui lòng chọn ảnh!" }]}
              style={{ textAlign: "center", marginBottom: 0 }}
            >
              <Upload
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
                beforeUpload={beforeUpload}
                maxCount={1}
                accept="image/*"
                className="upload-list-centered"
              >
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Space direction="vertical" style={{ width: "100%" }} size={spacing[4]}>
              <Form.Item
                label="Tên quà tặng"
                name="name"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "Vui lòng nhập tên quà tặng!" }]}
              >
                <Input placeholder="Tên chương trình (VD: Suất ăn Combo 1...)" />
              </Form.Item>

              <Form.Item
                label="Đơn vị quản lý"
                name="mcoupon_branch_id"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "Vui lòng chọn đơn vị!" }]}
              >
                <TreeSelect
                  allowClear
                  showSearch
                  treeData={storeOptions}
                  placeholder="Chọn đơn vị"
                  loading={isLoadStoreAll}
                  filterTreeNode={(input, node) => (node.title || "").toLowerCase().includes(input.toLowerCase())}
                  onChange={(value) => setBranchId(value)}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: `${spacing[6]}px 0` }} />

        {/* Section 2: Nội dung & Loại quà tặng */}
        <div style={sectionTitle}>Nội dung & Loại quà tặng</div>
        <Row gutter={[spacing[4], spacing[4]]}>
          <Col span={24}>
            <Form.Item
              label="Phân loại"
              name="type"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Vui lòng chọn loại quà tặng!" }]}
            >
              <Select onChange={setGiftType}>
                <Select.Option value="PRODUCT">Sản phẩm (Vật lý/Dịch vụ)</Select.Option>
                {/* <Select.Option value="DATA">Dữ liệu (Thẻ cào/Data)</Select.Option> */}
              </Select>
            </Form.Item>
          </Col>

          {giftType === "PRODUCT" && (
            <Col span={24}>
              <Form.Item
                label="Sản phẩm áp dụng"
                required
                help="Nhấn nút bên dưới để chọn sản phẩm từ kho."
                style={{ marginBottom: 0 }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: spacing[3] }}>
                    <Tooltip title={!branchId ? "Vui lòng chọn Đơn vị quản lý trước" : ""}>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => setIsSelectorOpen(true)}
                        style={{ width: "fit-content" }}
                        disabled={!branchId}
                      >
                        Quản lý sản phẩm
                      </Button>
                    </Tooltip>

                    <Tooltip title={!branchId ? "Vui lòng chọn Đơn vị quản lý trước" : ""}>
                      <Button
                        type="dashed"
                        icon={<PlusCircleOutlined />}
                        onClick={() => setIsQuickAddOpen(true)}
                        style={{ width: "fit-content" }}
                        disabled={!branchId}
                      >
                        Thêm mới sản phẩm
                      </Button>
                    </Tooltip>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Divider type="vertical" style={{ height: 32 }} />
                      <Space size={spacing[1]}>
                        <Text style={{ fontSize: typography.size.base, color: neutral[700] }}>Kiểm soát kho:</Text>
                        <Form.Item name="is_inventory_check" valuePropName="checked" noStyle>
                          <Switch checked={isInventoryCheck} onChange={setIsInventoryCheck} size="small" />
                        </Form.Item>
                      </Space>
                    </div>
                  </div>

                  {selectedProductDetails.length > 0 && (
                    <div style={{ background: neutral[50], padding: spacing[4], borderRadius: radius.md, border: border.hairline }}>
                      <Text style={{ fontSize: typography.size.base, color: neutral[500], marginBottom: spacing[3], display: "block" }}>
                        <InfoCircleOutlined /> Sản phẩm đã chọn ({selectedProductDetails.length}):
                      </Text>
                      <Row gutter={[spacing[3], spacing[3]]}>
                        {selectedProductDetails.map((product) => (
                          <Col span={24} key={product.id}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                background: neutral[0],
                                padding: `${spacing[2]}px ${spacing[4]}px`,
                                borderRadius: radius.base,
                                border: border.hairline,
                              }}
                            >
                              <Space size="middle" style={{ flex: 1 }}>
                                {product.image && (
                                  <Image
                                    src={resolveImageUrl(product.image)}
                                    width={40}
                                    height={40}
                                    className="rounded object-cover"
                                    preview={false}
                                  />
                                )}
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                  <Text style={{ fontSize: typography.size.base, fontWeight: typography.weight.medium, color: neutral[900] }}>{product.name}</Text>
                                  <Text style={{ fontSize: typography.size.xs, color: neutral[500] }}>{product.code} | Còn lại: {product.remain_stock}</Text>
                                </div>
                              </Space>

                              <Space>
                                <Text style={{ fontSize: typography.size.sm, color: neutral[500] }}>Số lượng trong suất:</Text>
                                <InputNumber
                                  min={1}
                                  size="small"
                                  value={productQuantities[product.id] || 1}
                                  onChange={(val) => setProductQuantities((prev) => ({ ...prev, [product.id]: val }))}
                                  style={{ width: 65 }}
                                />
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    const newDetails = selectedProductDetails.filter((p) => p.id !== product.id);
                                    setSelectedProductDetails(newDetails);
                                    form.setFieldsValue({ products: newDetails.map((p) => p.id) });
                                    setProductQuantities((prev) => {
                                      const next = { ...prev };
                                      delete next[product.id];
                                      return next;
                                    });
                                  }}
                                />
                              </Space>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </div>
              </Form.Item>

              <ProductSelectorModal
                open={isSelectorOpen}
                onCancel={() => setIsSelectorOpen(false)}
                branchId={branchId}
                selectedIds={form.getFieldValue("products") || []}
                onSelect={(rows, keys) => {
                  setSelectedProductDetails(rows);
                  form.setFieldsValue({ products: keys });
                  setProductQuantities((prev) => {
                    const next = { ...prev };
                    keys.forEach((id) => {
                      if (!next[id]) next[id] = 1;
                    });
                    return next;
                  });
                }}
              />

              <ProductForm
                open={isQuickAddOpen}
                onCancel={() => setIsQuickAddOpen(false)}
                branchId={branchId}
                categories={categories}
                onSuccess={(newProduct) => {
                  setIsQuickAddOpen(false);
                  if (newProduct && newProduct.id) {
                    setSelectedProductDetails((prev) => {
                      if (!prev.find((p) => p.id === newProduct.id)) {
                        return [...prev, newProduct];
                      }
                      return prev;
                    });
                    const currentProducts = form.getFieldValue("products") || [];
                    if (!currentProducts.includes(newProduct.id)) {
                      form.setFieldsValue({ products: [...currentProducts, newProduct.id] });
                    }
                    setProductQuantities((prev) => ({ ...prev, [newProduct.id]: 1 }));
                    message.success(`Đã tự động thêm sản phẩm: ${newProduct.name}`);
                  }
                }}
              />
            </Col>
          )}

          {giftType === "DATA" && (
            <>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Nhà mạng"
                  name="telco"
                  style={{ marginBottom: 0 }}
                  rules={[{ required: true, message: "Vui lòng chọn nhà mạng!" }]}
                >
                  <Select
                    placeholder="Viettel, Mobifone..."
                    options={listTelcoDataGifts?.map((telco) => ({
                      label: telco.name,
                      value: telco.key,
                    }))}
                    onChange={setSelectedTelco}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Gói cước"
                  name="data_pack"
                  style={{ marginBottom: 0 }}
                  rules={[{ required: true, message: "Vui lòng chọn gói!" }]}
                >
                  <Select
                    placeholder="Chọn gói"
                    options={
                      selectedTelco
                        ? listDataGifts?.map((data) => ({
                            label: data.package_name,
                            value: data.data_pack,
                          }))
                        : []
                    }
                    disabled={!selectedTelco}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Divider style={{ margin: `${spacing[6]}px 0` }} />

        {/* Section 3: Điều kiện, Hạn mức & Trạng thái */}
        <div style={sectionTitle}>Điều kiện, Hạn mức & Trạng thái</div>
        <Row gutter={[spacing[4], spacing[4]]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Điểm quy đổi"
              name="point"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Vui lòng nhập điểm!" }]}
            >
              <Input type="number" prefix={<TrophyOutlined style={{ color: semantic.warning }} />} suffix="điểm" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Hạn mức kho"
              name="stock"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Vui lòng nhập hạn mức!" }]}
            >
              <Input type="number" suffix="quà" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Bắt đầu"
              name="start_date"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Vui lòng chọn ngày!" }]}
            >
              <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Kết thúc"
              name="end_date"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Vui lòng chọn ngày!" }]}
            >
              <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Row gutter={[spacing[3], spacing[3]]}>
              <Col xs={24} md={12}>
                <div style={{ border: border.hairline, borderRadius: radius.base, background: neutral[50], padding: `${spacing[3]}px ${spacing[4]}px` }}>
                  <Row justify="space-between" align="middle">
                    <Col><Text style={{ fontWeight: typography.weight.medium, color: neutral[800] }}>Giới hạn lượt</Text></Col>
                    <Col>
                      <Form.Item name="unlimited" valuePropName="checked" noStyle>
                        <Switch checked={unlimited} onChange={setUnlimited} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ border: border.hairline, borderRadius: radius.base, background: neutral[50], padding: `${spacing[3]}px ${spacing[4]}px` }}>
                  <Row justify="space-between" align="middle">
                    <Col><Text style={{ fontWeight: typography.weight.medium, color: neutral[800] }}>Kích hoạt</Text></Col>
                    <Col>
                      <Form.Item name="status" valuePropName="checked" noStyle>
                        <Switch checked={isActive} onChange={setIsActive} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>

      <style jsx>{`
        .upload-list-centered .ant-upload-select-picture-card {
          margin: 0 auto !important;
        }
      `}</style>
    </FormShell>
  );
}
